import type { Context as AuthContext } from "@sal/authentication.react";

export interface RefreshResponse {
	success?: boolean;
	contactDetails?: Record<string, unknown>;
}

/**
 * Admin2's mid-session token refresh: `POST /PortalFramework/Account/Refresh` re-issues `SWA-A` and
 * returns the caller's contact details (including their permissions), which we store back into
 * `auth.user`.
 *
 * Unlike the CV customer portal there is no follow-up permissions re-sync here: Admin2 has no
 * account scoping, so the permissions SALCore returns from `Refresh` are already the complete set.
 * If you find yourself wanting a second call to reconcile permissions, that is a sign account
 * scoping has crept in — see `IAdminUserContext` on the server side.
 *
 * Registered as `@sal/webrequest-web`'s `onRefreshToken` handler by
 * `components/SessionExpiryProvider.tsx`, which invokes it automatically whenever a
 * `sandfieldFetch`-based request 401s.
 *
 * Returns `true` if the session is confirmed valid afterwards, `false` when the refresh itself
 * failed — i.e. both `SWA-A` and `SWA-R` are gone and the session is genuinely dead.
 */
export async function refreshAdminSession(
	auth: Pick<AuthContext, "user" | "storeUserDetails">
): Promise<boolean> {
	let refreshJson: RefreshResponse | undefined;
	try {
		const response = await fetch("/PortalFramework/Account/Refresh", { method: "POST" });
		if (!response.ok) {
			return false;
		}
		refreshJson = (await response.json()) as RefreshResponse;
		if (!refreshJson?.success) {
			return false;
		}
	} catch {
		return false;
	}

	if (refreshJson.contactDetails) {
		auth.storeUserDetails(refreshJson.contactDetails);
	}
	return true;
}
